import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  applyExperienceCssVars,
  resolveProfile,
  type EmotionalProfile,
} from "@/lib/tenants/experienceProfiles";

export interface TenantConfig {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  country_code: string | null;
  subdomain: string | null;
  influencer_name: string | null;
  influencer_id: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  affiliate_links: Record<string, unknown>;
  feature_flags: Record<string, unknown>;
  is_active: boolean;
  emotional_profile?: Partial<EmotionalProfile> | null;
  experience_version?: number | null;
  sportsbook_name?: string | null;
  sportsbook_logo?: string | null;
  sportsbook_affiliate_url?: string | null;
  sportsbook_cta?: string | null;
  sportsbook_campaign?: string | null;
  sportsbook_theme?: Record<string, unknown> | null;
  sportsbook_priority?: number | null;
}

const DEFAULT_TENANT: TenantConfig = {
  tenant_id: "00000000-0000-0000-0000-000000000000",
  tenant_slug: "versesport-global",
  tenant_name: "VerseSport Global",
  country_code: "GLOBAL",
  subdomain: "www",
  influencer_name: null,
  influencer_id: null,
  logo_url: null,
  primary_color: "#3B82F6",
  secondary_color: "#0F172A",
  affiliate_links: {},
  feature_flags: {},
  is_active: true,
};

interface TenantContextValue {
  tenant: TenantConfig;
  profile: EmotionalProfile;
  loading: boolean;
  refresh: () => Promise<void>;
}

const TenantCtx = createContext<TenantContextValue>({
  tenant: DEFAULT_TENANT,
  profile: resolveProfile("GLOBAL"),
  loading: false,
  refresh: async () => {},
});

function resolveSubdomain(): string {
  if (typeof window === "undefined") return "www";
  const host = window.location.hostname;
  // Skip IPs and localhost
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return "www";
  const parts = host.split(".");
  if (parts.length < 3) return "www";
  const sub = parts[0]?.toLowerCase() ?? "www";
  // Lovable preview / project subdomains → fallback to global
  if (sub.startsWith("id-preview--") || sub.startsWith("project--") || sub === "versesport") {
    return "www";
  }
  return sub;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantConfig>(DEFAULT_TENANT);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const sub = resolveSubdomain();
      const { data } = await supabase
        .from("tenants_config")
        .select("*")
        .eq("is_active", true)
        .eq("subdomain", sub)
        .maybeSingle();
      if (data) {
        setTenant({ ...DEFAULT_TENANT, ...(data as Partial<TenantConfig>) });
      } else {
        // fallback global
        const { data: g } = await supabase
          .from("tenants_config")
          .select("*")
          .eq("tenant_slug", "versesport-global")
          .maybeSingle();
        if (g) setTenant({ ...DEFAULT_TENANT, ...(g as Partial<TenantConfig>) });
      }
    } catch (err) {
      console.warn("[TenantContext] resolve failed, using default", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // Persist slug for client-side helpers / referrals
    // Re-run on visibility regain in case admin toggled active flag
    const onVis = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply dynamic branding (CSS vars) — non-destructive overrides only.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (tenant.primary_color) root.style.setProperty("--tenant-primary", tenant.primary_color);
    if (tenant.secondary_color) root.style.setProperty("--tenant-secondary", tenant.secondary_color);
    const profile = resolveProfile(tenant.country_code, tenant.emotional_profile ?? null);
    applyExperienceCssVars(profile);
    try {
      localStorage.setItem("vs:tenant_slug", tenant.tenant_slug);
    } catch {
      /* ignore */
    }
  }, [tenant]);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenant,
      profile: resolveProfile(tenant.country_code, tenant.emotional_profile ?? null),
      loading,
      refresh: load,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenant, loading],
  );

  return <TenantCtx.Provider value={value}>{children}</TenantCtx.Provider>;
}

export function useTenant() {
  return useContext(TenantCtx);
}

export function getStoredTenantSlug(): string {
  if (typeof window === "undefined") return "versesport-global";
  try {
    return localStorage.getItem("vs:tenant_slug") || "versesport-global";
  } catch {
    return "versesport-global";
  }
}